import React, { useRef, useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  PenTool, 
  Eraser, 
  Check, 
  X,
  RotateCcw
} from 'lucide-react';

const SignaturePad = ({ onSign, onCancel, devisInfo }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signataireNom, setSignataireNom] = useState('');
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    canvas.width = 500;
    canvas.height = 200;
    
    // Style
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1E293B';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    // Draw signature line
    ctx.strokeStyle = '#E2E8F0';
    ctx.beginPath();
    ctx.moveTo(30, 160);
    ctx.lineTo(470, 160);
    ctx.stroke();
    ctx.strokeStyle = '#1E293B';
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Redraw signature line
    ctx.strokeStyle = '#E2E8F0';
    ctx.beginPath();
    ctx.moveTo(30, 160);
    ctx.lineTo(470, 160);
    ctx.stroke();
    ctx.strokeStyle = '#1E293B';
    
    setHasSignature(false);
  };

  const handleSign = () => {
    if (!signataireNom.trim()) {
      alert('Veuillez entrer le nom du signataire');
      return;
    }
    if (!hasSignature) {
      alert('Veuillez dessiner votre signature');
      return;
    }
    
    const canvas = canvasRef.current;
    const signatureImage = canvas.toDataURL('image/png');
    
    onSign({
      signature_image: signatureImage,
      signataire_nom: signataireNom.trim()
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Signature électronique</h2>
            <p className="text-sm text-slate-500">Bon pour accord du devis</p>
          </div>
          <button 
            onClick={onCancel}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        {/* Devis info */}
        {devisInfo && (
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-slate-600">
              <span className="font-medium">Devis :</span> {devisInfo.objet}
            </p>
            <p className="text-sm text-slate-600">
              <span className="font-medium">Montant TTC :</span> {devisInfo.total_ttc?.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
            </p>
          </div>
        )}

        {/* Nom du signataire */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Nom du signataire *
          </label>
          <Input
            type="text"
            value={signataireNom}
            onChange={(e) => setSignataireNom(e.target.value)}
            placeholder="Prénom et nom"
            className="w-full"
          />
        </div>

        {/* Signature canvas */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Signature *
          </label>
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-2 bg-slate-50">
            <canvas
              ref={canvasRef}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              className="w-full cursor-crosshair touch-none"
              style={{ maxHeight: '200px' }}
            />
          </div>
          <div className="flex items-center gap-2 mt-2">
            <PenTool className="h-4 w-4 text-slate-400" />
            <span className="text-xs text-slate-500">
              Dessinez votre signature avec la souris ou le doigt
            </span>
          </div>
        </div>

        {/* Clear button */}
        <div className="flex justify-end mb-6">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearSignature}
            className="text-slate-500"
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Effacer
          </Button>
        </div>

        {/* Legal text */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
          <p className="text-xs text-amber-800">
            En signant ce document, je reconnais avoir pris connaissance des conditions générales 
            et j'accepte le présent devis. Cette signature électronique a valeur de consentement 
            conformément aux dispositions du Code civil.
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
          >
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleSign}
            className="bg-green-600 hover:bg-green-700"
          >
            <Check className="h-4 w-4 mr-2" />
            Valider la signature
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SignaturePad;
